import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Select,
  Spinner,
  Tab,
  TabList,
  Tabs,
  Text,
  VStack,
} from '@chakra-ui/react'
import { childTicketService, type TaskTemplate } from '@/lib/services/childTicketService'
import {
  taskTemplateService,
  type TaskTemplateDefaults,
} from '@/lib/services/taskTemplateService'
import { useToastNotification } from '@/lib/utils/useToastNotification'

type CreateMode = 'template' | 'blank'

type Props = {
  isOpen: boolean
  onClose: () => void
  parentTicketId: string
  onCreated: () => void | Promise<void>
}

const BLANK_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'awaiting_customer_response', label: 'Awaiting customer response' },
  { value: 'cancelled', label: 'Cancelled' },
] as const

export default function CreateTaskFromTemplateDrawer({
  isOpen,
  onClose,
  parentTicketId,
  onCreated,
}: Props) {
  const { showError, showSuccess } = useToastNotification()
  const [mode, setMode] = useState<CreateMode>('template')
  const [templates, setTemplates] = useState<TaskTemplate[]>([])
  const [loadingTemplates, setLoadingTemplates] = useState(false)
  const [selectedCode, setSelectedCode] = useState('')
  const [defaults, setDefaults] = useState<TaskTemplateDefaults | null>(null)
  const [loadingDefaults, setLoadingDefaults] = useState(false)
  const [documentPurpose, setDocumentPurpose] = useState('')
  const [blankTitle, setBlankTitle] = useState('')
  const [blankStatus, setBlankStatus] = useState('pending')
  const [blankAssignedTo, setBlankAssignedTo] = useState('')
  const [creating, setCreating] = useState(false)

  const creatableTemplates = useMemo(
    () =>
      templates.filter((template) => {
        const code = template.code?.trim()
        if (!code) return false
        const stepCount = template.steps?.length ?? 0
        return stepCount === 0 || template.is_sequential === false
      }),
    [templates]
  )

  const loadTemplates = useCallback(async () => {
    setLoadingTemplates(true)
    try {
      const items = await childTicketService.listTemplates()
      setTemplates(items)
    } catch (err) {
      showError(err as Error)
    } finally {
      setLoadingTemplates(false)
    }
  }, [showError])

  useEffect(() => {
    if (!isOpen) return
    setMode('template')
    setSelectedCode('')
    setDefaults(null)
    setDocumentPurpose('')
    setBlankTitle('')
    setBlankStatus('pending')
    setBlankAssignedTo('')
    void loadTemplates()
  }, [isOpen, loadTemplates])

  useEffect(() => {
    if (!isOpen || mode !== 'template' || !selectedCode) {
      setDefaults(null)
      return
    }

    let cancelled = false
    setLoadingDefaults(true)
    const context =
      selectedCode === 'document_collection' && documentPurpose.trim()
        ? { document_purpose: documentPurpose.trim() }
        : undefined

    taskTemplateService
      .getDefaults(selectedCode, parentTicketId, context)
      .then((resolved) => {
        if (!cancelled) setDefaults(resolved)
      })
      .catch((err) => {
        if (!cancelled) {
          setDefaults(null)
          showError(err as Error)
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingDefaults(false)
      })

    return () => {
      cancelled = true
    }
  }, [documentPurpose, isOpen, mode, parentTicketId, selectedCode, showError])

  const handleModeChange = (index: number) => {
    const nextMode: CreateMode = index === 1 ? 'blank' : 'template'
    setMode(nextMode)
    if (nextMode === 'blank') {
      setSelectedCode('')
      setDefaults(null)
      setDocumentPurpose('')
    } else {
      setBlankTitle('')
      setBlankStatus('pending')
      setBlankAssignedTo('')
    }
  }

  const handleCreate = async () => {
    if (mode === 'blank') {
      if (!blankTitle.trim()) {
        showError(new Error('Title is required'))
        return
      }
      setCreating(true)
      try {
        await childTicketService.create(parentTicketId, {
          title: blankTitle.trim(),
          status: blankStatus,
          assigned_to: blankAssignedTo ? Number(blankAssignedTo) : null,
        })
        showSuccess('Task created')
        await onCreated()
        onClose()
      } catch (err) {
        showError(err as Error)
      } finally {
        setCreating(false)
      }
      return
    }

    if (!selectedCode) {
      showError(new Error('Select a task template'))
      return
    }
    setCreating(true)
    try {
      const context =
        selectedCode === 'document_collection' && documentPurpose.trim()
          ? { document_purpose: documentPurpose.trim() }
          : undefined
      await taskTemplateService.createTaskFromTemplate(parentTicketId, {
        template_code: selectedCode,
        context,
      })
      showSuccess('Task created')
      await onCreated()
      onClose()
    } catch (err) {
      showError(err as Error)
    } finally {
      setCreating(false)
    }
  }

  const createDisabled =
    mode === 'blank' ? !blankTitle.trim() : !selectedCode || !defaults

  return (
    <Drawer isOpen={isOpen} placement="right" size="md" onClose={onClose}>
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader>Create task</DrawerHeader>
        <DrawerBody>
          <VStack align="stretch" spacing={4}>
            <Tabs
              size="sm"
              index={mode === 'blank' ? 1 : 0}
              onChange={handleModeChange}
              variant="enclosed"
            >
              <TabList>
                <Tab>From template</Tab>
                <Tab>Blank task</Tab>
              </TabList>
            </Tabs>

            {mode === 'template' ? (
              <>
                <Text fontSize="sm" color="gray.600">
                  Pick a standalone task template. Workflow templates with multiple steps should still be applied via
                  &quot;Apply template&quot;.
                </Text>

                {loadingTemplates ? (
                  <HStack py={6} justify="center">
                    <Spinner size="sm" />
                    <Text fontSize="sm">Loading templates…</Text>
                  </HStack>
                ) : (
                  <FormControl isRequired>
                    <FormLabel>Template</FormLabel>
                    <Select
                      placeholder={
                        creatableTemplates.length ? 'Select template' : 'No standalone templates available'
                      }
                      value={selectedCode}
                      onChange={(e) => setSelectedCode(e.target.value)}
                    >
                      {creatableTemplates.map((template) => (
                        <option key={template.id} value={template.code ?? ''}>
                          {template.name}
                          {template.code ? ` (${template.code})` : ''}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {selectedCode === 'document_collection' ? (
                  <FormControl>
                    <FormLabel>Document purpose</FormLabel>
                    <Select
                      value={documentPurpose || 'collection'}
                      onChange={(e) => setDocumentPurpose(e.target.value)}
                    >
                      <option value="collection">Collection</option>
                      <option value="validation">Validation</option>
                      <option value="approval">Approval</option>
                    </Select>
                  </FormControl>
                ) : null}

                {loadingDefaults ? (
                  <HStack py={4} justify="center">
                    <Spinner size="sm" />
                    <Text fontSize="sm">Resolving defaults…</Text>
                  </HStack>
                ) : null}

                {defaults ? (
                  <Box p={4} borderWidth="1px" borderRadius="md" bg="gray.50">
                    <Text fontWeight="600" mb={3}>
                      Resolved defaults
                    </Text>
                    <VStack align="stretch" spacing={2} fontSize="sm">
                      <HStack justify="space-between" align="flex-start">
                        <Text color="gray.600">Title</Text>
                        <Text textAlign="right" maxW="65%">
                          {defaults.title || '—'}
                        </Text>
                      </HStack>
                      <HStack justify="space-between" align="flex-start">
                        <Text color="gray.600">Status</Text>
                        <Text>{defaults.status || '—'}</Text>
                      </HStack>
                      <HStack justify="space-between" align="flex-start">
                        <Text color="gray.600">Priority</Text>
                        <Text>{defaults.priority || '—'}</Text>
                      </HStack>
                      <HStack justify="space-between" align="flex-start">
                        <Text color="gray.600">Due</Text>
                        <Text>{defaults.due_at ? new Date(defaults.due_at).toLocaleString() : '—'}</Text>
                      </HStack>
                      {defaults.notes ? (
                        <Box>
                          <Text color="gray.600" mb={1}>
                            Notes
                          </Text>
                          <Text whiteSpace="pre-wrap">{defaults.notes}</Text>
                        </Box>
                      ) : null}
                      {(defaults.form_fields?.length ?? 0) > 0 ? (
                        <Box>
                          <Text color="gray.600" mb={1}>
                            Modules
                          </Text>
                          <Text>{defaults.form_fields?.map((field) => field.module).join(', ')}</Text>
                        </Box>
                      ) : null}
                    </VStack>
                  </Box>
                ) : null}
              </>
            ) : (
              <>
                <Text fontSize="sm" color="gray.600">
                  Create a simple task without a template. You can edit details after it is created.
                </Text>

                <FormControl isRequired>
                  <FormLabel>Title</FormLabel>
                  <Input
                    value={blankTitle}
                    onChange={(e) => setBlankTitle(e.target.value)}
                    placeholder="Task description"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Status</FormLabel>
                  <Select value={blankStatus} onChange={(e) => setBlankStatus(e.target.value)}>
                    {BLANK_STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>Assigned to (user ID)</FormLabel>
                  <Input
                    value={blankAssignedTo}
                    onChange={(e) => setBlankAssignedTo(e.target.value)}
                    placeholder="Optional — leave blank to assign later"
                    type="number"
                    min={1}
                  />
                </FormControl>
              </>
            )}
          </VStack>
        </DrawerBody>
        <DrawerFooter>
          <Button variant="outline" mr={3} onClick={onClose}>
            Close
          </Button>
          <Button
            colorScheme="blue"
            onClick={() => void handleCreate()}
            isLoading={creating}
            isDisabled={createDisabled}
          >
            Create task
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}
